/**
 * Password Generator Service
 * Cryptographically secure password generation using Web Crypto API
 */

// ==================== Types ====================

/** Password generation configuration */
export interface PasswordGeneratorConfig {
  /** Minimum password length */
  readonly minLength: number;
  /** Maximum password length */
  readonly maxLength: number;
  /** Include uppercase letters (A-Z) */
  readonly includeUppercase: boolean;
  /** Include lowercase letters (a-z) */
  readonly includeLowercase: boolean;
  /** Include numbers (0-9) */
  readonly includeNumbers: boolean;
  /** Include special characters */
  readonly includeSpecial: boolean;
}

// ==================== Constants ====================

/** Character sets for password generation */
export const CHARACTER_SETS = {
  UPPERCASE: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  LOWERCASE: 'abcdefghijklmnopqrstuvwxyz',
  NUMBERS: '0123456789',
  SPECIAL: '!@#$%^&*()_+-=[]{}|;:,.<>?',
} as const;

/** Password length constraints */
export const PASSWORD_LENGTH_LIMITS = {
  MIN: 4,
  MAX: 128,
} as const;

/** Default password configuration for strong passwords */
export const DEFAULT_PASSWORD_CONFIG: Readonly<PasswordGeneratorConfig> = {
  minLength: 16,
  maxLength: 24,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSpecial: true,
} as const;

// ==================== Validation ====================

/**
 * Validates password configuration
 * @param config - The configuration to validate
 * @returns Error message or null if valid
 */
export function validatePasswordConfig(config: PasswordGeneratorConfig): string | null {
  const { minLength, maxLength, includeUppercase, includeLowercase, includeNumbers, includeSpecial } = config;

  if (minLength < PASSWORD_LENGTH_LIMITS.MIN) {
    return `Minimum length must be at least ${PASSWORD_LENGTH_LIMITS.MIN}`;
  }

  if (maxLength > PASSWORD_LENGTH_LIMITS.MAX) {
    return `Maximum length cannot exceed ${PASSWORD_LENGTH_LIMITS.MAX}`;
  }

  if (minLength > maxLength) {
    return 'Minimum length cannot be greater than maximum length';
  }

  const enabledCount = [includeUppercase, includeLowercase, includeNumbers, includeSpecial].filter(Boolean).length;

  if (enabledCount === 0) {
    return 'At least one character type must be enabled';
  }

  if (minLength < enabledCount) {
    return `Minimum length must be at least ${enabledCount} to include one of each selected character type`;
  }

  return null;
}

// ==================== Generation ====================

/**
 * Generates a cryptographically secure random integer using rejection sampling
 * to eliminate modulo bias.
 * @param max - Maximum value (exclusive), must be > 0
 * @returns Random integer between 0 and max-1 with uniform distribution
 */
function getSecureRandomInt(max: number): number {
  if (max <= 0) {
    throw new Error('max must be greater than 0');
  }

  // For uniform distribution, reject values that would cause modulo bias
  // The threshold is the largest multiple of max that fits in uint32
  const maxUint32 = 0xFFFFFFFF;
  const threshold = maxUint32 - (maxUint32 % max);

  const array = new Uint32Array(1);
  let value: number;

  // Rejection sampling: discard values >= threshold to eliminate bias
  do {
    crypto.getRandomValues(array);
    value = array[0];
  } while (value >= threshold);

  return value % max;
}

/**
 * Shuffles a string securely using Fisher-Yates algorithm
 * @param str - String to shuffle
 * @returns Shuffled string
 */
function shuffleString(str: string): string {
  const chars = str.split('');
  for (let i = chars.length - 1; i > 0; i--) {
    const j = getSecureRandomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

/**
 * Gets a random character from a string
 * @param charSet - String of characters to choose from
 * @returns Random character
 */
function getRandomChar(charSet: string): string {
  return charSet[getSecureRandomInt(charSet.length)];
}

/**
 * Generates a secure random password based on configuration
 * @param config - Password generation settings
 * @returns Generated password string
 * @throws Error if no character sets are enabled
 */
export function generatePassword(config: PasswordGeneratorConfig): string {
  const validationError = validatePasswordConfig(config);
  if (validationError) {
    throw new Error(validationError);
  }

  const { minLength, maxLength, includeUppercase, includeLowercase, includeNumbers, includeSpecial } = config;

  // Calculate random length within range
  const length = minLength === maxLength
    ? minLength
    : minLength + getSecureRandomInt(maxLength - minLength + 1);

  // Build character pool and collect required characters
  let charPool = '';
  const requiredChars: string[] = [];

  if (includeUppercase) {
    charPool += CHARACTER_SETS.UPPERCASE;
    requiredChars.push(getRandomChar(CHARACTER_SETS.UPPERCASE));
  }

  if (includeLowercase) {
    charPool += CHARACTER_SETS.LOWERCASE;
    requiredChars.push(getRandomChar(CHARACTER_SETS.LOWERCASE));
  }

  if (includeNumbers) {
    charPool += CHARACTER_SETS.NUMBERS;
    requiredChars.push(getRandomChar(CHARACTER_SETS.NUMBERS));
  }

  if (includeSpecial) {
    charPool += CHARACTER_SETS.SPECIAL;
    requiredChars.push(getRandomChar(CHARACTER_SETS.SPECIAL));
  }

  // Fill remaining length with random characters from pool
  const remainingLength = length - requiredChars.length;
  const randomChars: string[] = [];

  for (let i = 0; i < remainingLength; i++) {
    randomChars.push(getRandomChar(charPool));
  }

  // Combine required and random characters, then shuffle
  const password = shuffleString([...requiredChars, ...randomChars].join(''));

  return password;
}

/**
 * Calculates password strength score (0-100)
 * @param password - Password to evaluate
 * @returns Strength score
 */
export function calculatePasswordStrength(password: string): number {
  let score = 0;

  // Length scoring (up to 40 points)
  score += Math.min(password.length * 2, 40);

  // Character variety scoring (up to 40 points)
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);

  const varietyCount = [hasUppercase, hasLowercase, hasNumbers, hasSpecial].filter(Boolean).length;
  score += varietyCount * 10;

  // Bonus for longer passwords (up to 20 points)
  if (password.length >= 16) score += 10;
  if (password.length >= 24) score += 10;

  return Math.min(score, 100);
}

/**
 * Gets strength label based on score
 * @param score - Strength score (0-100)
 * @returns Human-readable strength label
 */
export function getStrengthLabel(score: number): 'weak' | 'fair' | 'good' | 'strong' | 'very strong' {
  if (score < 40) return 'weak';
  if (score < 60) return 'fair';
  if (score < 80) return 'good';
  if (score < 90) return 'strong';
  return 'very strong';
}
